import { Grid } from '@factorialco/deprecated-design-system'
import { ContractVersionPageContractVersionFragment } from 'generated/resources/graphql'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Section from 'modules/contracts/components/ContractDetails/Section'
import { getCountryField } from 'modules/contracts/lib/getCountryField'

import ContractType from './ContractType'
import EndDate from './EndDate'
import StartDate from './StartDate'
import TrialPeriod from './TrialPeriod'

interface Props {
  contractVersion: ContractVersionPageContractVersionFragment
  isVersionDataEnabled: boolean
  isContractPaused: boolean
  isEmployeeTerminated: boolean
}

const ContractInformation = ({
  contractVersion,
  isVersionDataEnabled,
  isContractPaused,
  isEmployeeTerminated,
}: Props) => {
  const canEdit = contractVersion._policies?.edit.can ?? false
  const canSeeContractType = contractVersion._policies?.seeContractType.can ?? false
  const canSeeEndDate = contractVersion._policies?.seeEndDate.can ?? false
  const canSeeStartDate = contractVersion._policies?.seeStartDate.can ?? false
  const canSeeTrial = contractVersion._policies?.seeTrialPeriod.can ?? false
  const canSeeTrialEnd = contractVersion._policies?.seeTrialPeriodEndDate.can ?? false

  const contractTypeName =
    getCountryField({
      isVersionDataEnabled,
      contractVersion,
      field: 'contract_type',
    })?.name ?? null
  const hasTrial = contractVersion.hasTrialPeriod ?? false
  const showStartDate = canSeeStartDate && (contractVersion.startsOn || canEdit)
  const showEndDate = canSeeEndDate && (contractVersion.endsOn || canEdit)
  const showContractType = canSeeContractType && (contractTypeName || canEdit)
  const startsOnDate = contractVersion.startsOn
    ? stringToDate(contractVersion.startsOn, 'backend')
    : null
  const endsOnDate =
    contractVersion.endsOn && !isContractPaused
      ? stringToDate(contractVersion.endsOn, 'backend')
      : null
  const trialPeriodEndDate =
    hasTrial && contractVersion.trialPeriodEndsOn
      ? stringToDate(contractVersion.trialPeriodEndsOn, 'backend')
      : null

  return (
    <Section icon="person" title={i18n.t('contract_versions.contract_information.title')}>
      <Grid variant="fixed">
        {showStartDate && <StartDate startDate={startsOnDate} />}
        {showContractType && <ContractType contractTypeName={contractTypeName} />}
        {showEndDate && (
          <EndDate
            endDate={endsOnDate}
            canEdit={canEdit}
            isEmployeeTerminated={isEmployeeTerminated}
          />
        )}
        {canSeeTrial && (
          <TrialPeriod
            hasTrial={hasTrial}
            canEdit={canEdit}
            canSeeTrialEnd={canSeeTrialEnd}
            trialPeriod={trialPeriodEndDate}
          />
        )}
      </Grid>
    </Section>
  )
}

export default ContractInformation
